import type { LocationAddress } from './locations.server';

/**
 * Minimal structural shape of a ticket variant used by the availability
 * domain. Keeps availability free of any `~/domain/experiences` import while
 * remaining compatible with that domain's `TicketVariant` type (callers can
 * pass either).
 */
export type TicketVariantShape = {
  id: string;
  addOns: { id: string }[];
};

export type AvailabilityStatus =
  | 'available'
  | 'limited'
  | 'soldOut'
  | 'unavailable';

/**
 * Whether an event with this status can still be booked. `soldOut` and
 * `unavailable` events cannot. Used to anchor calendars / date pickers on the
 * first *bookable* date rather than the first date with any event.
 */
export function isBookableStatus(status: AvailabilityStatus): boolean {
  return status === 'available' || status === 'limited';
}

/**
 * Whether views may show an event's `availableSeats`:
 * - `hideSeatCount` — never: the no-limit capacity sentinel is not a number the
 *   visitor can act on, so no surface should show or announce it.
 * - `bookingMaxBelowSeatCount` — the per-booking maximum is below the seats
 *   left, so the count disagrees with what one basket accepts (US-10996). Shown
 *   only when the storefront's seat counter is set to always show (US-14091).
 *
 * Both are presentational only — the scarcity band that produces `'limited'`
 * deliberately ignores booking rules (US-10832).
 */
type SeatCountFlags = {
  hideSeatCount: boolean;
  bookingMaxBelowSeatCount: boolean;
};

export type EventAvailability = SeatCountFlags &
  (
    | { status: 'available'; availableSeats: number }
    | { status: 'limited'; availableSeats: number }
    | { status: 'soldOut'; availableSeats: 0 }
    | { status: 'unavailable'; availableSeats: number }
  );

export type EventSession = {
  date: string;
  startTime: string;
  endTime: string | null;
  locales: string[];
  location: LocationAddress | null;
};

export type UpcomingEvent = {
  eventId: string;
  /**
   * Top-level date/startTime/endTime/locales describe the *first* session.
   * Kept stable so single-session callers (checkout, basket guards) don't
   * need to change. For multi-session events, see `sessions` for the full
   * list.
   */
  date: string;
  startTime: string;
  endTime: string | null;
  /** All sessions on this event, in start-time order. Length 1 for single-session experiences. */
  sessions: EventSession[];
  location: LocationAddress | null;
  locales: string[];
  totalSeats: number;
  availability: EventAvailability;
  description: string | null;
};

export type DateGroup = {
  date: string;
  events: UpcomingEvent[];
};

/**
 * What an availability slot points at. A slot is either a concrete, scheduled
 * event (a "fixed event") or an open-hour window that has no event yet.
 */
export type SlotRef =
  | { kind: 'event'; eventId: string }
  | {
      kind: 'open';
      /** The operating hours the slot belongs to — resolve the location
       *  through them (slots no longer carry a location of their own). */
      operatingHoursId: string;
      /** ISO 8601 local wall-clock start of the open slot (no UTC offset);
       *  with `operatingHoursId` + `durationSeconds` this is the slot's
       *  deterministic key. */
      startTime: string;
      /** Booking duration this open slot covers, in seconds. */
      durationSeconds: number;
    };

/**
 * A single bookable slot from availability-core. `available` is the
 * authoritative "AND of all constraints" — the source of truth for whether
 * the slot can be booked. Times are ISO 8601 strings.
 *
 * Slots carry no location or languages of their own (US-10738): location
 * resolves through the referenced event or operating hours; languages
 * through events (fixed) or, eventually, operating hours (open).
 */
export type AvailabilitySlot = {
  ref: SlotRef;
  /** ISO 8601 local wall-clock start (no UTC offset); interpret against `timeZone`. */
  start: string;
  /** ISO 8601 local wall-clock end (no UTC offset); interpret against `timeZone`. */
  end: string;
  /** IANA time zone the wall-clock times are expressed in, e.g. "Europe/Copenhagen". */
  timeZone: string;
  available: boolean;
};

/**
 * An availability slot stitched with its originating event (when it references
 * one). A fixed-event slot always carries its `UpcomingEvent`: one whose event
 * the feed withheld is dropped during stitching rather than kept without an
 * event, so the type mirrors the rule. Open slots are self-contained.
 */
export type StitchedSlot =
  | { kind: 'event'; slot: AvailabilitySlot; event: UpcomingEvent }
  | { kind: 'open'; slot: AvailabilitySlot };

export type FilterOption = {
  value: string;
  label: string;
};

export type FilterOptions = {
  languages: FilterOption[];
  locations: FilterOption[];
};
